import React, { useEffect, useState } from 'react';
import {
    Checkbox,
    Typography,
    Space,
    Spin,
    Empty,
    Button,
    Tag,
    Modal,
    Form,
    Input,
    Select,
    Popconfirm,
    Tooltip,
    message,
} from 'antd';
import { GlobalOutlined, PlusOutlined, EditOutlined, StopOutlined, DeleteOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
    getPropertyDirectory,
    updatePropertyGlobalContacts,
    createPropertyContact,
    updateContact,
    archiveContact,
    deleteContact,
} from '../../config/services/patrolService';
import { parseApiError, bindFormErrors } from '../../config/services/errorHandler';
import { useSelector } from 'react-redux';
import useColors from '../../hooks/useColors';
import { Colors } from '../../config';

const { Text } = Typography;
const { Option } = Select;

const phoneTypeLabels = {
    mobile: 'Mobile',
    landline: 'Landline',
    fax: 'Fax',
    work: 'Work',
    other: 'Other',
};

/**
 * PropertyPhoneDirectoryPanel — company-wide global contacts (checkbox selection,
 * scoped to this property) + property-only phone numbers (full CRUD).
 * Backend: GET/PATCH /properties/{id}/directory[/global-contacts], and the shared
 * /directory/contacts endpoints for creating/editing/archiving/deleting a number.
 */
const PropertyPhoneDirectoryPanel = ({ propertyId }) => {
    const { colorPrimary } = useColors();
    const queryClient = useQueryClient();
    const user = useSelector((state) => state.AppReducer?.user);
    const isRole = user?.userRole?.[0];
    const canWrite = isRole === 'admin' || isRole === 'manager';

    const [selectedIds, setSelectedIds] = useState(null);
    const [modalState, setModalState] = useState({ open: false, contact: null });
    const [form] = Form.useForm();

    const { data, isLoading } = useQuery(
        ['property-directory', propertyId],
        () => getPropertyDirectory(propertyId),
        {
            enabled: !!propertyId,
            onError: (err) => message.error(parseApiError(err).message),
        }
    );

    // Sync local selection from the server whenever fresh data lands.
    useEffect(() => {
        if (data) setSelectedIds(data.selectedGlobalContactIds ?? []);
    }, [data]);

    const globalContacts = data?.globalContacts ?? [];
    const siteContacts = (data?.siteContacts ?? []).filter((c) => !c.archivedAt);

    const selectionDirty =
        selectedIds !== null &&
        JSON.stringify([...selectedIds].sort((a, b) => a - b)) !==
            JSON.stringify([...(data?.selectedGlobalContactIds ?? [])].sort((a, b) => a - b));

    const toggleGlobal = (contactId) => {
        setSelectedIds((prev) => {
            const current = prev ?? [];
            return current.includes(contactId)
                ? current.filter((x) => x !== contactId)
                : [...current, contactId];
        });
    };

    const saveSelectionMutation = useMutation(
        () => updatePropertyGlobalContacts(propertyId, selectedIds ?? []),
        {
            onSuccess: () => {
                message.success('Global contacts updated.');
                queryClient.invalidateQueries(['property-directory', propertyId]);
            },
            onError: (err) => message.error(parseApiError(err).message),
        }
    );

    // ── Site contact create / update ─────────────────────────
    const saveContactMutation = useMutation(
        (body) => {
            const { id, ...rest } = body;
            return id ? updateContact(id, rest) : createPropertyContact(propertyId, rest);
        },
        {
            onSuccess: () => {
                message.success(modalState.contact ? 'Number updated.' : 'Number added.');
                queryClient.invalidateQueries(['property-directory', propertyId]);
                closeModal();
            },
            onError: (err) => {
                const parsed = parseApiError(err);
                if (parsed.fields) bindFormErrors(form, parsed.fields);
                else message.error(parsed.message);
            },
        }
    );

    const archiveMutation = useMutation((id) => archiveContact(id), {
        onSuccess: () => {
            message.success('Number archived.');
            queryClient.invalidateQueries(['property-directory', propertyId]);
        },
        onError: (err) => message.error(parseApiError(err).message),
    });

    const deleteMutation = useMutation((id) => deleteContact(id), {
        onSuccess: () => {
            message.success('Number permanently deleted.');
            queryClient.invalidateQueries(['property-directory', propertyId]);
        },
        onError: (err) => message.error(parseApiError(err).message),
    });

    const openCreate = () => {
        setModalState({ open: true, contact: null });
        form.resetFields();
        form.setFieldsValue({ phoneType: 'mobile' });
    };

    const openEdit = (contact) => {
        setModalState({ open: true, contact });
        form.setFieldsValue({
            fullName: contact.fullName,
            jobTitle: contact.jobTitle,
            mobileNumber: contact.mobileNumber,
            phoneType: contact.phoneType,
            email: contact.email,
        });
    };

    const closeModal = () => {
        setModalState({ open: false, contact: null });
        form.resetFields();
    };

    const handleSubmit = (values) => {
        if (modalState.contact) {
            // PUT is a full replace — carry over fields this compact form doesn't expose.
            saveContactMutation.mutate({
                id: modalState.contact.id,
                scope: 'site',
                fullName: values.fullName || 'Phone Number',
                jobTitle: values.jobTitle || null,
                mobileNumber: values.mobileNumber,
                phoneType: values.phoneType,
                email: values.email || null,
                propertyIds: [Number(propertyId)],
                isActive: true,
            });
        } else {
            saveContactMutation.mutate({
                mobileNumber: values.mobileNumber,
                phoneType: values.phoneType,
                fullName: values.fullName || undefined,
                jobTitle: values.jobTitle || undefined,
                email: values.email || undefined,
            });
        }
    };

    return (
        <Spin spinning={isLoading}>
            {/* Global contacts */}
            <div style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
                    <Space size={6}>
                        <GlobalOutlined style={{ color: '#6366f1' }} />
                        <Text strong>Global Contacts</Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Select which company-wide contacts apply to this property
                        </Text>
                    </Space>
                    {canWrite && selectionDirty && (
                        <Button
                            type="primary"
                            size="small"
                            loading={saveSelectionMutation.isLoading}
                            onClick={() => saveSelectionMutation.mutate()}
                            style={{ backgroundColor: colorPrimary, border: 'none', color: '#fff' }}
                        >
                            Save Selection
                        </Button>
                    )}
                </div>

                {globalContacts.length === 0 ? (
                    <Empty description="No global contacts" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                ) : (
                    <Space direction="vertical" size={6}>
                        {globalContacts.map((c) => (
                            <Checkbox
                                key={c.id}
                                disabled={!canWrite}
                                checked={(selectedIds ?? []).includes(c.id)}
                                onChange={() => toggleGlobal(c.id)}
                            >
                                <Text strong>{c.fullName}</Text>
                                {c.jobTitle && (
                                    <Text type="secondary" style={{ fontSize: 12, marginLeft: 6 }}>
                                        ({c.jobTitle})
                                    </Text>
                                )}
                                <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                                    {c.mobileNumber || c.email || '—'}
                                </Text>
                            </Checkbox>
                        ))}
                    </Space>
                )}
            </div>

            <hr />

            {/* Site-only phone numbers */}
            <div style={{ marginTop: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                    <Text strong>Property Phone Numbers</Text>
                    {canWrite && (
                        <Button
                            type="primary"
                            size="small"
                            icon={<PlusOutlined />}
                            onClick={openCreate}
                            style={{ backgroundColor: colorPrimary, border: 'none', color: '#fff' }}
                        >
                            Add
                        </Button>
                    )}
                </div>

                {siteContacts.length === 0 ? (
                    <Empty description="No property phone numbers" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                ) : (
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
                            gap: 12,
                        }}
                    >
                        {siteContacts.map((c) => (
                            <div
                                key={c.id}
                                className="flex items-center justify-between gap-4 px-4 py-3 rounded-lg"
                                style={{ background: Colors.LightGray }}
                            >
                                <div>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        {c.fullName || 'Phone Number'}
                                        {c.jobTitle && ` (${c.jobTitle})`}
                                    </Text>
                                    <div className="flex items-center gap-2 mt-1">
                                        <p className="text-sm font-medium break-all" style={{ margin: 0 }}>
                                            {c.mobileNumber}
                                        </p>
                                        <Tag style={{ margin: 0 }}>
                                            {phoneTypeLabels[c.phoneType] ?? c.phoneType}
                                        </Tag>
                                    </div>
                                </div>
                                {canWrite && (
                                    <Space size={4}>
                                        <Tooltip title="Edit">
                                            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(c)} />
                                        </Tooltip>
                                        <Popconfirm
                                            title="Archive this number?"
                                            description="Removed from active views but not permanently deleted."
                                            okText="Archive"
                                            onConfirm={() => archiveMutation.mutate(c.id)}
                                        >
                                            <Tooltip title="Archive (recommended)">
                                                <Button size="small" icon={<StopOutlined />} loading={archiveMutation.isLoading} />
                                            </Tooltip>
                                        </Popconfirm>
                                        {isRole === 'admin' && (
                                            <Popconfirm
                                                title="Permanently delete this number?"
                                                description="This cannot be undone. Archive is recommended instead."
                                                okText="Delete Permanently"
                                                okButtonProps={{ danger: true }}
                                                onConfirm={() => deleteMutation.mutate(c.id)}
                                            >
                                                <Tooltip title="Permanent delete (admin only)">
                                                    <Button size="small" icon={<DeleteOutlined />} danger loading={deleteMutation.isLoading} />
                                                </Tooltip>
                                            </Popconfirm>
                                        )}
                                    </Space>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Add / Edit modal */}
            <Modal
                title={modalState.contact ? 'Edit Phone Number' : 'Add Phone Number'}
                open={modalState.open}
                onCancel={closeModal}
                footer={null}
                width={420}
                destroyOnClose
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    <Form.Item
                        label="Full Name"
                        name="fullName"
                        rules={[{ max: 255, message: 'Max 255 characters.' }]}
                    >
                        <Input placeholder="e.g. Front Desk (optional, defaults to Phone Number)" />
                    </Form.Item>
                    <Form.Item
                        label="Job Title"
                        name="jobTitle"
                        rules={[{ max: 255, message: 'Max 255 characters.' }]}
                    >
                        <Input placeholder="Optional" />
                    </Form.Item>
                    <Form.Item
                        label="Phone Number"
                        name="mobileNumber"
                        rules={[
                            { required: true, message: 'Phone number is required.' },
                            { max: 40, message: 'Max 40 characters.' },
                        ]}
                    >
                        <Input placeholder="(032) 005-0987" />
                    </Form.Item>
                    <Form.Item
                        label="Phone Type"
                        name="phoneType"
                        rules={[{ required: true, message: 'Required.' }]}
                    >
                        <Select placeholder="Select type">
                            {Object.entries(phoneTypeLabels).map(([value, label]) => (
                                <Option key={value} value={value}>{label}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item
                        label="Email"
                        name="email"
                        rules={[
                            { type: 'email', message: 'Enter a valid email.' },
                            { max: 255, message: 'Max 255 characters.' },
                        ]}
                    >
                        <Input placeholder="Optional" />
                    </Form.Item>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                        <Button onClick={closeModal}>Cancel</Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={saveContactMutation.isLoading}
                            style={{ backgroundColor: colorPrimary, border: 'none', color: '#fff' }}
                        >
                            {modalState.contact ? 'Save Changes' : 'Add Number'}
                        </Button>
                    </div>
                </Form>
            </Modal>
        </Spin>
    );
};

export default PropertyPhoneDirectoryPanel;
